import { useState } from 'react';
import { Button, Checkbox, Dialog, InlineError, Input, Select } from '../../components/ui';
import { useChatPeople, useCreateDm, useCreateGroupRoom } from './hooks';
import { GROUP_MAX_PEOPLE, GROUP_NAME_MAX, roomErrorMessage } from './rooms';

/**
 * "New direct message" and "New group chat" (mockup/Main.dc.html:58, :63, the
 * two + buttons). One dialog, two shapes.
 *
 * The people offered are exactly the ones create_dm / create_group_room
 * accept (20270180000100): active TAP admins and members, agents included
 * (agent accounts are tap_member). No viewers, no suppliers, never yourself.
 * The database still refuses anything else; the list only avoids offering a
 * choice that would be refused.
 *
 * Opening a DM that already exists returns that DM (create_dm dedupes from
 * either side), so "New direct message" to someone you already talk to just
 * takes you there.
 */

export type NewChatMode = 'dm' | 'group';

export function NewChatDialog({
  mode,
  myId,
  onClose,
  onCreated,
}: {
  mode: NewChatMode;
  myId: string;
  onClose: () => void;
  /** Called with the room id to open. */
  onCreated: (roomId: string) => void;
}) {
  const people = useChatPeople(true);
  const createDm = useCreateDm();
  const createGroup = useCreateGroupRoom();

  const [personId, setPersonId] = useState('');
  const [name, setName] = useState('');
  const [filter, setFilter] = useState('');
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [error, setError] = useState('');

  const others = (people.data ?? []).filter((person) => person.id !== myId);
  const needle = filter.trim().toLocaleLowerCase();
  const shown =
    needle === ''
      ? others
      : others.filter((person) => person.name.toLocaleLowerCase().includes(needle));
  const pending = createDm.isPending || createGroup.isPending;

  function report(cause: unknown) {
    setError(roomErrorMessage(cause instanceof Error ? cause.message : ''));
  }

  function toggle(id: string) {
    setPicked((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function submit() {
    setError('');
    if (mode === 'dm') {
      if (personId === '') {
        setError('Pick who to message.');
        return;
      }
      createDm.mutate(personId, { onSuccess: onCreated, onError: report });
      return;
    }
    const trimmed = name.trim();
    if (trimmed === '') {
      setError('Give the group a name.');
      return;
    }
    if (trimmed.length > GROUP_NAME_MAX) {
      setError(`Keep the name to ${GROUP_NAME_MAX} characters.`);
      return;
    }
    if (picked.size === 0) {
      setError('Add at least one other person.');
      return;
    }
    if (picked.size + 1 > GROUP_MAX_PEOPLE) {
      setError(`A group holds at most ${GROUP_MAX_PEOPLE} people, you included.`);
      return;
    }
    createGroup.mutate(
      { name: trimmed, memberIds: [...picked] },
      { onSuccess: onCreated, onError: report },
    );
  }

  return (
    <Dialog open title={mode === 'dm' ? 'New direct message' : 'New group chat'} onClose={onClose}>
      <div className="new-chat-dialog">
        {error === '' ? null : <InlineError>{error}</InlineError>}
        {people.isError ? (
          <InlineError>The list of people could not be loaded. Try again.</InlineError>
        ) : null}

        {mode === 'dm' ? (
          <Select
            label="To"
            value={personId}
            onChange={(event) => setPersonId(event.target.value)}
            placeholder={people.isPending ? 'Loading people' : 'Choose a person'}
            options={others.map((person) => ({ value: person.id, label: person.name }))}
          />
        ) : (
          <>
            <Input
              label="Group name"
              value={name}
              maxLength={GROUP_NAME_MAX}
              onChange={(event) => setName(event.target.value)}
            />
            <Input
              label="Find people"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
            />
            <fieldset className="new-chat-people">
              <legend className="new-chat-people-legend">
                People ({picked.size} of {GROUP_MAX_PEOPLE - 1})
              </legend>
              {shown.length === 0 ? (
                <p className="new-chat-empty">
                  {people.isPending ? 'Loading people' : 'No one matches.'}
                </p>
              ) : (
                shown.map((person) => (
                  <Checkbox
                    key={person.id}
                    label={person.name}
                    checked={picked.has(person.id)}
                    onChange={() => toggle(person.id)}
                  />
                ))
              )}
            </fieldset>
          </>
        )}

        <div className="new-chat-actions">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={pending}>
            {pending ? 'Starting…' : mode === 'dm' ? 'Open chat' : 'Create group'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
